import {
  Activity,
  Ban,
  BookOpen,
  Bot,
  Crown,
  FileText,
  LayoutDashboard,
  type LucideIcon,
  MessageSquare,
  Settings,
  Star,
  Users,
  Wrench,
} from 'lucide-react';
import { NavLink } from 'react-router';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/authStore';

interface NavItem {
  name: string;
  href: string;
  icon: LucideIcon;
}

const navigation: NavItem[] = [
  { name: 'Обзор', href: '/', icon: LayoutDashboard },
  { name: 'Заявки', href: '/submissions', icon: FileText },
  { name: 'Баны', href: '/bans', icon: Ban },
  { name: 'Апелляции', href: '/appeals', icon: MessageSquare },
  { name: 'Активность', href: '/activity', icon: Activity },
  { name: 'База знаний', href: '/knowledge-base', icon: BookOpen },
  { name: 'AI Настройки', href: '/ai-settings', icon: Bot },
];

const adminNavigation: NavItem[] = [
  { name: 'Администраторы', href: '/admins', icon: Users },
  { name: 'Настройки', href: '/settings', icon: Settings },
];

export function Sidebar() {
  const admin = useAuthStore((s) => s.admin);
  const isAdmin = admin?.role === 'owner' || admin?.role === 'admin';

  const getRoleIcon = () => {
    switch (admin?.role) {
      case 'owner':
        return Crown;
      case 'admin':
        return Star;
      default:
        return Wrench;
    }
  };

  const RoleIcon = getRoleIcon();

  return (
    <aside className="w-64 min-h-screen bg-cyber-dark border-r border-cyber-light flex flex-col">
      {/* Logo */}
      <div className="p-6 border-b border-cyber-light">
        <h1 className="font-display text-xl text-neon-cyan tracking-wider">
          KINO
          <span className="text-white">HELPDESK</span>
        </h1>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-1">
        {navigation.map((item) => (
          <NavLink
            key={item.href}
            to={item.href}
            className={({ isActive }) =>
              cn(
                'flex items-center gap-3 px-4 py-2.5 rounded-lg transition-all duration-200',
                isActive
                  ? 'bg-neon-cyan/10 text-neon-cyan border border-neon-cyan/30'
                  : 'text-gray-400 hover:text-gray-100 hover:bg-cyber-gray',
              )
            }
          >
            <item.icon className="w-4 h-4" />
            <span className="text-sm font-medium">{item.name}</span>
          </NavLink>
        ))}

        {isAdmin && (
          <>
            <div className="pt-4 pb-2">
              <div className="text-xs uppercase tracking-wider text-gray-500 px-4">
                Администрирование
              </div>
            </div>
            {adminNavigation.map((item) => (
              <NavLink
                key={item.href}
                to={item.href}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-3 px-4 py-2.5 rounded-lg transition-all duration-200',
                    isActive
                      ? 'bg-neon-magenta/10 text-neon-magenta border border-neon-magenta/30'
                      : 'text-gray-400 hover:text-gray-100 hover:bg-cyber-gray',
                  )
                }
              >
                <item.icon className="w-4 h-4" />
                <span className="text-sm font-medium">{item.name}</span>
              </NavLink>
            ))}
          </>
        )}
      </nav>

      {/* User info */}
      <div className="p-4 border-t border-cyber-light">
        <div className="cyber-card p-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-neon-cyan/10 flex items-center justify-center text-neon-cyan">
              <RoleIcon className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-100 truncate">
                @{admin?.telegram_username}
              </p>
              <p className="text-xs text-gray-500 capitalize">{admin?.role}</p>
            </div>
          </div>
        </div>
      </div>
    </aside>
  );
}
